// Data access layer for the dashboard map page.
//
// This is the ONLY module that knows whether the data comes from the mock or from the
// backend. The map components import from here and never touch axios or the mock directly.
//
// The real branch is written against the Phase-2 endpoints of the dashboard concept
// (/live/active, /aggregates/kbs, /geo/kbs). Several of them do not exist yet — see
// docs/dashboard-map-api.md for the contract and the open gaps. Until they are deployed,
// USE_MOCK stays true on localhost.

import { api } from './trainUtils';
import * as mock from './mockDashboardApi';

export const MOCKUP_ENABLED = true;

const isLocalhost =
  window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

// REACT_APP_USE_MOCK overrides the localhost default in both directions. The end-to-end
// tests need this: "false" makes them talk to the real backend even on localhost, "true"
// lets them run against the mock anywhere.
const mockOverride = window._env_?.REACT_APP_USE_MOCK ?? process.env.REACT_APP_USE_MOCK;

export const USE_MOCK = mockOverride === undefined || mockOverride === ''
  ? MOCKUP_ENABLED && isLocalhost
  : String(mockOverride).toLowerCase() === 'true';

export { toOperatingDay, shiftOperatingDay } from './mockDashboardApi';

// Map the frontend train types (classifyTrainType) onto the aggregate train_group values
export const TYPE_TO_GROUP = {
  fern: 'fern',
  regio: 'nah',
  sbahn: 'sbahn',
  ubahn: 'sonstige',
  tram: 'sonstige',
  bus: 'sonstige',
  default: 'sonstige',
};

const bboxParam = (bbox) => (bbox ? bbox.join(',') : undefined);

const filterParams = (filters) => ({
  train_group: filters?.types?.length
    ? Array.from(new Set(filters.types.map(t => TYPE_TO_GROUP[t] || 'sonstige'))).join(',')
    : undefined,
  line: filters?.line || undefined,
  serie: filters?.serie || undefined,
  station: filters?.station || undefined,
  kbs: filters?.kbs || undefined,
});

/** Train positions at a point in time. GET /live/active */
export async function fetchActiveTrains({ at, bbox, filters } = {}) {
  if (USE_MOCK) return mock.fetchActiveTrains({ at, bbox, filters });

  const now = at instanceof Date ? at : new Date(at || Date.now());
  const res = await api.get('/live/active', {
    params: { at: now.toISOString(), bbox: bboxParam(bbox), ...filterParams(filters) },
  });
  const trains = Array.isArray(res.data) ? res.data : res.data?.trains || [];
  return { trains, total: trains.length, at: now, operatingDate: mock.toOperatingDay(now) };
}

/** Traffic-light state per KBS for an operating day. GET /aggregates/kbs?date= */
export async function fetchKbsStatus({ opDay, filters } = {}) {
  if (USE_MOCK) return mock.fetchKbsStatus({ opDay, filters });

  const res = await api.get('/aggregates/kbs', {
    params: { date: opDay, ...filterParams(filters) },
  });
  return Array.isArray(res.data) ? res.data : res.data?.kbs || [];
}

/** Drill-in for a single KBS. GET /aggregates/kbs?kbs=&date= */
export async function fetchKbsDetail({ kbsNumber, opDay, filters } = {}) {
  if (USE_MOCK) return mock.fetchKbsDetail({ kbsNumber, opDay, filters });
  // Without a number the endpoint answers the list form — 113 summary rows, none of them
  // carrying the hourly, segment and top-delayed blocks the drill-in renders. Taking the
  // first of those looks like a detail object right up to the point where it is read.
  if (!kbsNumber) return null;

  // A route without trains on that day answers 404 "No data for this KBS and day". That is
  // an answer, not a failure — /geo/kbs knows 341 routes, a day's aggregate about 135, and
  // the current day none until it has been computed. Null says "no data"; real errors throw.
  // The filters go first, as in fetchStationStats. They carry a `kbs` of their own — the map's
  // route filter, undefined when unset — and spread last it overwrote the route this request
  // is about. axios drops undefined, so the endpoint saw no route and answered the list form:
  // every drill-in came back as "no data", whatever the day held.
  let res;
  try {
    res = await api.get('/aggregates/kbs', {
      params: { ...filterParams(filters), date: opDay, kbs: kbsNumber, detail: 1 },
    });
  } catch (err) {
    if (err?.response?.status === 404) return null;
    throw err;
  }
  const row = Array.isArray(res.data) ? res.data[0] : res.data;
  return row && 'hourly' in row ? row : null;
}

/**
 * Station statistics for one operating day, plus the previous day and a 7-day trend.
 * GET /aggregates/station — same figures as the per-station block of the daily report.
 */
export async function fetchStationStats({ stationUUID, stationName, opDay, filters, trendDays } = {}) {
  if (USE_MOCK) return mock.fetchStationStats({ stationName, opDay, filters, trendDays });
  // The endpoint resolves the station by UUID; a name answers 404 and a missing value 422
  if (!stationUUID) return null;

  // The filters are spread first on purpose. They carry a `station` of their own — the map's
  // "only trains touching this station" filter — and spreading them last overwrote the
  // station this request is *about* with an undefined, which axios then dropped: the endpoint
  // saw no station at all and answered 422. Two different meanings, same parameter name.
  const res = await api.get('/aggregates/station', {
    params: { ...filterParams(filters), station: stationUUID, date: opDay, trend_days: trendDays },
  });
  return res.data;
}

/** Stations to draw on the map — the recipient's station set */
export async function fetchStations() {
  if (USE_MOCK) return mock.fetchStations();

  // /v1/stations pages with a default of 50 — the map needs the whole set
  const res = await api.get('/v1/stations', { params: { limit: 5000 } });
  return (Array.isArray(res.data) ? res.data : [])
    .filter(s => s.lat && s.lng && s.stationName)
    .map(s => ({
      // The UUID is what the timetable board is keyed on
      uuid: s.UUID,
      id: s.globalStationID,
      name: s.stationName.trim(),
      lat: s.lat,
      lng: s.lng,
    }));
}

/** Arrivals or departures at one station. GET /v1/stations/{uuid}/timetable */
export async function fetchStationBoard({ stationUUID, at, type = 'departure', limit = 50, offset = 0 } = {}) {
  if (USE_MOCK) return mock.fetchStationBoard({ stationUUID, at, type, limit, offset });
  if (!stationUUID) return [];

  const when = at instanceof Date ? at : new Date(at || Date.now());
  const params = { sort_by: type === 'arrival' ? 'arrival' : 'departure', limit, offset };
  // The board follows the map: in the point-in-time mode it must not show "now"
  if (Math.abs(when.getTime() - Date.now()) > 5 * 60 * 1000) {
    params.start = when.toISOString();
  }

  const res = await api.get(`/v1/stations/${stationUUID}/timetable`, { params });
  return Array.isArray(res.data) ? res.data : [];
}

/** Train search. GET /v1/trains — same parameters the standalone search uses */
export async function searchTrains({
  query, operatingDate, trainLine, trainSerie, trainNumber, trainTypeProductCategorie, limit,
} = {}) {
  if (USE_MOCK) {
    return mock.searchTrains({
      query, operatingDate, trainLine, trainSerie, trainNumber, trainTypeProductCategorie, limit,
    });
  }

  // The search runs in the database, not in the browser. It used to fetch a page of rows —
  // 50 by default — and filter those here, so anything outside that page simply did not
  // exist as far as the search was concerned. ICE 105 was one of them: running, visible on
  // the map, not findable.
  //
  // `trainNameLong` is a contains match and is the only field that works. `trainNumber` is
  // empty on every row we sampled, so the old branch that matched a numeric query against it
  // could never return anything either.
  const needle = (query || '').trim();
  const base = {
    operatingDate: operatingDate || undefined,
    trainLine: trainLine || undefined,
    trainSerie: trainSerie || undefined,
    trainNumber: trainNumber || undefined,
    trainTypeProductCategorie: trainTypeProductCategorie || undefined,
    limit: limit || 50,
  };

  const res = await api.get('/v1/trains', {
    params: { ...base, trainNameLong: needle || undefined },
  });
  const byName = Array.isArray(res.data) ? res.data : [];
  if (byName.length || !needle) return byName;

  // Nothing by name — try the operator. A FlixTrain run is called "FLX 1237", so someone
  // typing "Flix" gets nothing from the name alone, and that is a fair thing to search for.
  // Two requests rather than one filter, because the endpoint combines its filters with AND.
  try {
    const byOperator = await api.get('/v1/trains', {
      params: { ...base, trainSerieOperator: needle },
    });
    return Array.isArray(byOperator.data) ? byOperator.data : [];
  } catch {
    return byName;
  }
}

/**
 * Network-wide statistics for one operating day, plus the previous day and the punctuality
 * trend — the daily operations report as a dashboard.
 * GET /aggregates/total?date= together with /aggregates/kbs; see docs/dashboard-map-api.md.
 */
export async function fetchNetworkStats({ opDay, filters, trendDays, trendMonths } = {}) {
  if (USE_MOCK) return mock.fetchNetworkStats({ opDay, filters, trendDays, trendMonths });

  const res = await api.get('/aggregates/total', {
    params: {
      date: opDay,
      trend_days: trendDays,
      trend_months: trendMonths,
      ...filterParams(filters),
    },
  });
  return res.data;
}

// The route geometry is static and the filter options need the same list for the KBS
// dropdown. It used to be fetched twice at start-up — half a megabyte each, in two of the six
// connections the browser grants a host while the train positions waited for one to free up.
let kbsGeometryRequest = null;

/** KBS geometry. GET /geo/kbs — fetched once per session */
export async function fetchKbsGeometry() {
  if (USE_MOCK) return mock.fetchKbsGeometry();

  if (!kbsGeometryRequest) {
    kbsGeometryRequest = api.get('/geo/kbs')
      .then(res => (Array.isArray(res.data) ? res.data : res.data?.kbs || []))
      .catch(err => { kbsGeometryRequest = null; throw err; });
  }
  return kbsGeometryRequest;
}

/**
 * Track geometry between neighbouring stops, so trains follow the rails instead of cutting
 * across country. Keyed "fromStationID-toStationID"; pairs without geometry are absent from
 * the answer and keep the straight line.
 *
 * Requested apart from the positions on purpose: the course of a section belongs to the
 * track, not to the train, so it is the same on every poll and for every train over it.
 * The backend serves it with a day of cache time, so a section costs one request per client.
 */
export async function fetchRouteSegments(pairs) {
  if (!pairs?.length) return {};
  if (USE_MOCK) return mock.fetchRouteSegments(pairs);

  const res = await api.get('/geo/segments', { params: { pairs: pairs.join(',') } });
  return res.data && typeof res.data === 'object' ? res.data : {};
}

/**
 * Full run of one train — stops with times plus route geometry. Reuses the two endpoints the
 * train detail view already calls, so nothing new is needed on the backend for this.
 */
/**
 * How this train has run over the past months. GET /live/train-history
 *
 * Keyed on the line, not on the run: the same ICE 599 leaves at the same minute every day
 * under a new globalTrainID, and the question is about the service, not about today's train.
 *
 * The train's UUID goes with it so the API can anchor on where this run ends. A line name
 * alone is not an identity — "S1" is one row for Berlin, Rhein-Main, Karlsruhe and
 * Rhein-Neckar together — and anchoring on a station the run calls at does not work either:
 * which stations get polled varies by day, so it cut 88 measured days down to one.
 */
export async function fetchTrainHistory(train, days = 90) {
  if (USE_MOCK) return mock.fetchTrainHistory(train, days);
  const uuid = train?.trainUUID;
  const line = train?.trainLineName || train?.trainName;
  if (!uuid && !line) return null;
  const res = await api.get('/live/train-history', {
    params: uuid ? { train: uuid, days } : { line, days },
  });
  return res.data;
}

export async function fetchTrainJourney(train) {
  if (USE_MOCK) return mock.fetchTrainJourney(train);
  // /v1/trains/{uuid} is keyed on the UUID, not on the numeric globalTrainID — passing the
  // latter answers 404 for every train and leaves the panel loading forever.
  const uuid = train?.trainUUID || train?.UUID;
  if (!uuid) return null;

  const res = await api.get(`/v1/trains/${uuid}`);
  const journey = res.data?.journey;
  const stations = (journey?.journeyStations || [])
    .filter(s => s.lat && s.lng)
    .sort((a, b) => {
      const ta = new Date(a.departureTime_scheduled || a.arrivalTime_scheduled).getTime();
      const tb = new Date(b.departureTime_scheduled || b.arrivalTime_scheduled).getTime();
      return ta - tb;
    });

  let coords = stations.map(s => [s.lat, s.lng]);
  const journeyUUID = journey?.journeyUUID || train.journeyUUID;
  if (journeyUUID) {
    try {
      const loc = await api.get(`/v1/journeys/${journeyUUID}/locations-json`);
      const poly = loc.data?.polylineDescriptions
        ?.flatMap(p => p.coordinates?.map(c => [c.lat, c.lng]) || []) || [];
      if (poly.length >= 2) coords = poly;
    } catch {
      // geometry is optional — the station chain is a usable fallback
    }
  }

  return {
    // When the import last wrote this run, not when we asked for it. That is what says how
    // fresh the times are — our own request is always "just now" and tells the reader nothing.
    collectedAt: journey?.created || null,
    trainName: res.data?.trainName_long || train.trainName,
    trainNumber: res.data?.trainNumber,
    trainLineName: res.data?.trainLineName,
    trainTypeProductCategorie: res.data?.trainTypeProductCategorie,
    trainTypeValue: res.data?.trainTypeValue,
    operatingDate: res.data?.operatingDate || train.operatingDate,
    cancelled: journey?.journeyCancelled === 1,
    trainSeries: res.data?.trainSeries || [],
    attributes: journey?.journeyAttributes || [],
    messages: res.data?.stationRequest?.messages || [],
    stations,
    coords,
  };
}

/** Filter dropdown contents */
export async function fetchFilterOptions() {
  if (USE_MOCK) return mock.fetchFilterOptions();

  const [linesRes, seriesRes, stationsRes, kbsRes] = await Promise.all([
    api.get('/v1/train-lines').catch(() => ({ data: [] })),
    api.get('/v1/train-series').catch(() => ({ data: [] })),
    api.get('/v1/stations').catch(() => ({ data: [] })),
    fetchKbsGeometry().then(data => ({ data })).catch(() => ({ data: [] })),
  ]);

  const asArray = (d) => (Array.isArray(d) ? d : []);
  return {
    lines: asArray(linesRes.data).map(l => ({
      value: l.trainLineName, label: l.trainLineName,
    })),
    series: asArray(seriesRes.data).map(s => ({
      value: s.trainSerieNumber || s.UUID,
      label: s.trainSerieName || s.trainSerieNumber || s.name,
    })),
    stations: asArray(stationsRes.data).map(s => ({
      value: s.stationName, label: s.stationName,
    })),
    kbs: asArray(kbsRes.data).map(k => ({
      value: k.kbs_number, label: `${k.kbs_number} — ${k.kbs_name}`,
    })),
  };
}
